/**
 * Microsoft Clarity analytics helpers for the get-started flow.
 * Uses the Clarity client API: https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
 *
 * Best practices applied:
 * - Consistent event names for funnel analysis (survey_*, account_*, plan_*)
 * - Funnel tag so sessions can be filtered in Clarity by "get_started"
 * - Session upgrade on key steps so conversion sessions are prioritized for recording
 *
 * First event (e.g. survey_started) can fire before the Clarity script has run; we retry
 * briefly so it is not dropped.
 */

declare global {
	interface Window {
		clarity?: (method: string, ...args: string[]) => void
	}
}

const FUNNEL_TAG_KEY = 'funnel'
const FUNNEL_TAG_VALUE = 'get_started'

const CLARITY_READY_RETRY_MS = 50
const CLARITY_READY_MAX_WAIT_MS = 2500

function isClarityAvailable(): boolean {
	return typeof window !== 'undefined' && typeof window.clarity === 'function'
}

function sendClarityEvent(eventName: string): void {
	if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
		window.clarity('event', eventName)
	}
}

/**
 * Send a custom event to Microsoft Clarity. Events appear in Filters, Dashboard, and Recordings.
 * If Clarity is not ready yet (e.g. first event on page load), retries for a short period
 * so the event is not dropped.
 */
export function trackClarityEvent(eventName: string): void {
	if (typeof window === 'undefined') return

	if (isClarityAvailable()) {
		sendClarityEvent(eventName)
		return
	}

	// Clarity not ready yet (e.g. survey_started fires before clarity.init() / script runs)
	let elapsed = 0
	const intervalId = setInterval(() => {
		elapsed += CLARITY_READY_RETRY_MS
		if (isClarityAvailable()) {
			clearInterval(intervalId)
			sendClarityEvent(eventName)
		} else if (elapsed >= CLARITY_READY_MAX_WAIT_MS) {
			clearInterval(intervalId)
		}
	}, CLARITY_READY_RETRY_MS)
}

/**
 * Tag the session as part of the get-started funnel so you can filter in Clarity.
 * Call once when the user enters the flow (e.g. when survey step is shown).
 */
export function setClarityFunnelTag(): void {
	if (!isClarityAvailable()) return
	window.clarity!('set', FUNNEL_TAG_KEY, FUNNEL_TAG_VALUE)
}

/**
 * Prioritize this session for recording (Clarity may sample when over daily limit).
 * Use for key conversion steps so you don't lose high-intent sessions.
 */
export function upgradeClaritySession(reason: string): void {
	if (!isClarityAvailable()) return
	window.clarity!('upgrade', reason)
}
